import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MathMarkdown } from '@/components/chat/MathMarkdown';
import { FileText, Target, TrendingUp, AlertTriangle, CheckCircle, Lightbulb, Heart } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { logError } from '@/lib/logger';
import { Badge } from '@/components/ui/badge';

interface ChatSummary {
  topics: string[];
  concerns: string[];
  goals: string[];
  progress: string[];
  challenges: string[];
  successes: string[];
  description: string;
  sentiment?: { label?: 'positive' | 'neutral' | 'negative' | 'na'; intensity?: number | null };
}

interface ChatSummaryDetailModalProps {
  chatId: number;
  chatTitle: string;
  summary: ChatSummary | null;
  summaryUnavailable?: boolean;
  unavailableMessage?: string | null;
  trigger: React.ReactNode;
  generateEndpoint?: string; // se fornito, genera il summary all'apertura
  onGenerated?: (summary: ChatSummary) => void;
}

export default function ChatSummaryDetailModal({
  chatTitle,
  summary,
  summaryUnavailable = false,
  unavailableMessage,
  trigger,
  generateEndpoint,
  onGenerated,
}: ChatSummaryDetailModalProps) {
  const [open, setOpen] = useState(false);
  const [localSummary, setLocalSummary] = useState<ChatSummary | null>(summary);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const SummarySection = ({
    title,
    items,
    icon: Icon,
    color = 'bg-blue-50 dark:bg-blue-950/20',
  }: {
    title: string;
    items: string[];
    icon: LucideIcon;
    color?: string;
  }) => (
    <div className={`p-4 rounded-lg ${color}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      {items.length > 0 ? (
        <ul className="space-y-1">
          {items.map((item, index) => (
            <li key={index} className="text-sm text-muted-foreground">
              • {item}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground italic">
          Nessun elemento da mostrare
        </p>
      )}
    </div>
  );

  const handleOpenChange = async (o: boolean) => {
    setOpen(o);
    if (o && generateEndpoint && !localSummary && !summaryUnavailable) {
      try {
        setLoading(true);
        setError(null);
        const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
        const res = await fetch(generateEndpoint, {
          method: 'POST',
          headers: { 'X-CSRF-Token': csrf },
          credentials: 'include',
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          if (data?.message) throw new Error(String(data.message));
          throw new Error('Bad response');
        }
        const data = await res.json();
        if (data?.summary) {
          setLocalSummary(data.summary);
          onGenerated?.(data.summary);
        } else {
          throw new Error('Missing summary');
        }
      } catch (err) {
        logError('Errore nella generazione del riassunto', err);
        setError(err instanceof Error ? err.message : 'Errore nella generazione del riassunto');
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setLocalSummary(summary);
  }, [summary]);

  const renderSentiment = (sentiment?: ChatSummary['sentiment']) => {
    if (!sentiment || !sentiment.label) return null;
    const label = sentiment.label;
    const intensity = sentiment.intensity;
    const map = {
      positive: {
        text: 'Positivo',
        className: 'border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950/30 dark:text-green-200',
        icon: 'text-green-500',
      },
      neutral: {
        text: 'Neutrale',
        className: 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-200',
        icon: 'text-slate-400',
      },
      negative: {
        text: 'Negativo',
        className: 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200',
        icon: 'text-red-500',
      },
      na: {
        text: 'N/A',
        className: 'border-border bg-muted text-muted-foreground',
        icon: 'text-muted-foreground',
      },
    } as const;
    const data = map[label] || map.na;
    return (
      <Badge variant="outline" className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs ${data.className}`}>
        <Heart className={`h-3.5 w-3.5 ${data.icon}`} />
        {data.text}
        {intensity ? ` · ${intensity}/10` : ''}
      </Badge>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {React.isValidElement(trigger) ? trigger : <span>{trigger}</span>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Dettaglio Summary - {chatTitle}
          </DialogTitle>
          <DialogDescription>
            Analisi strutturata della conversazione con temi, obiettivi,
            progressi e sfide.
          </DialogDescription>
          {renderSentiment(localSummary?.sentiment)}
        </DialogHeader>
        {loading && <p className="text-sm text-muted-foreground">Generazione in corso…</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!localSummary && !loading && !error && !summaryUnavailable && (
          <p className="text-sm text-muted-foreground">Nessun riassunto disponibile.</p>
        )}

        {summaryUnavailable && !loading && !error && (
          <p className="text-sm text-muted-foreground">
            {unavailableMessage || 'Riassunto non disponibile.'}
          </p>
        )}

        {localSummary && (
          <div className="space-y-6">
          {/* Descrizione generale */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Descrizione Generale</h3>
            <div className="prose prose-sm max-w-none">
              <MathMarkdown>{localSummary.description}</MathMarkdown>
            </div>
          </div>

          {/* Grid delle sezioni */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SummarySection
              title="Temi Principali"
              items={localSummary.topics}
              icon={Lightbulb}
              color="bg-green-50 dark:bg-green-950/20"
            />

            <SummarySection
              title="Preoccupazioni"
              items={localSummary.concerns}
              icon={AlertTriangle}
              color="bg-red-50 dark:bg-red-950/20"
            />

            <SummarySection
              title="Obiettivi"
              items={localSummary.goals}
              icon={Target}
              color="bg-purple-50 dark:bg-purple-950/20"
            />

            <SummarySection
              title="Progressi"
              items={localSummary.progress}
              icon={TrendingUp}
              color="bg-blue-50 dark:bg-blue-950/20"
            />

            <SummarySection
              title="Sfide"
              items={localSummary.challenges}
              icon={AlertTriangle}
              color="bg-orange-50 dark:bg-orange-950/20"
            />

            <SummarySection
              title="Successi"
              items={localSummary.successes}
              icon={CheckCircle}
              color="bg-emerald-50 dark:bg-emerald-950/20"
            />
          </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
